import * as m from 'mithril';
import AppContext from '../context/AppContext';
import SearchCriteria, { LocationType } from '../models/SearchCriteria';
import { HomeStateFsmScheme } from '../states/home/HomeFsmScheme';
import ArticlePage from '../pages/article/ArticlePage';
import HomePage from '../pages/home/Home';
import { MessageProvider } from '../context/AppMessages';
import LocationSelectionPage from '../pages/locationselection/LocationSelectionPage';
import BookingDetailsPage from '../pages/mybookings/BookingDetailsPage';
import BookingListPage from '../pages/mybookings/BookingListPage';
import ContactPage from '../pages/contact/ContactPage';
import { SubFsm } from 'little-fsm';
import { getIntlStringValue } from '../helpers/StringHelpers';
import { ActionItemDescriptor } from '../components/MaterialDesignComponents';
import { ApiService } from '../ApiService';
import { BookingDTO, BookingSummaryDTO, InternationalStringDTO, LocationDTO, SourceLocationDTO } from '../apiclient/ApiDtos';
import SearchFormModel from '../models/SearchFormModel';

export function buildHomePresenter(
    appContext: AppContext,
    apiService: ApiService,
    sourceLocations: SourceLocationDTO[],
    shuttleSources: SourceLocationDTO[],
    subFsm: SubFsm<HomeStateFsmScheme>, 
    isAccountSupported: boolean = false, 
    isBackAllowed: boolean = false
): { render: () => m.Vnode } {

    let fetchingMyBookings: boolean = false;
    let myBookings: BookingSummaryDTO[] | null = null;
    let requestedBookingId: string | null = null;
    let currentMyBooking: BookingDTO | null = null;

    subFsm.setEnterEffect('fetching_article', ctx => {
        if (ctx.articleToLoad) {
            apiService.getArticle(ctx.articleToLoad?.slug).then(result => {
                if (result.right) {
                    console.log("Got article response", result.right);
                    subFsm.processEvent('article_received', { article: result.right });
                } else {
                    console.log("Got error", result.left);
                }
            });
        }
    });

    subFsm.setEnterEffect('fetching_my_booking_list', ctx => {
        fetchingMyBookings = true;
        apiService.searchBookings(0, 10).then(result => {
            if (result.right) {
                myBookings = result.right.data.sort((a, b) =>
                    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                fetchingMyBookings = false;
                subFsm.processEvent("my_bookings_received", { bookings: myBookings });
            }

        })
    })
    subFsm.setEnterEffect('showing_my_booking_list', ctx => {
        currentMyBooking = null;
        requestedBookingId = null;
    })
    subFsm.setEnterEffect('fetching_booking_details', ctx => {
        apiService.getBookingDetails(requestedBookingId!).then(result => {
            if (result.right) {
                subFsm.processEvent("booking_details_received", { booking: result.right });
                currentMyBooking = result.right
            }

        })
    })

    return {
        render() {
            let stateContext = subFsm.getCurrentContext();
            let substate = subFsm.getCurrentSubState();

            if (substate == 'initial') {

                return m(HomePage, {
                    shouldShakeNewFeatureTab: appContext.getShowAsShakeForNewFeatureTab(),
                    appContext: appContext,
                    sourceLocations: sourceLocations,
                    shuttleSources: shuttleSources,
                    isSearchingSmallCar: stateContext.isSearchingSmallCar,
                    searchFormModel: stateContext.searchFormModel,
                    isBackAllowed: isBackAllowed,
                    onLocationSelectionRequested: (locationType: LocationType) => {
                        subFsm.processEvent("location_requested", { locationType: locationType });
                    },
                    onChangeCarType: (isSearchingSmallCar: boolean) => {
                        if (stateContext.isSearchingSmallCar != isSearchingSmallCar) {
                            let updatedSearchFormModel: SearchFormModel = stateContext.searchFormModel;

                            if (stateContext.searchFormModel.sourceLocation != null) {
                                if (isSearchingSmallCar) {
                                    const matchedSource = shuttleSources.find(source => {
                                        return source.id === stateContext.searchFormModel.sourceLocation!.id
                                    })
                                    if (matchedSource != undefined) {
                                        let destination = stateContext.searchFormModel.destinationLocation == null ? null :
                                            matchedSource.destinations.find(dest => {
                                                return dest.id === stateContext.searchFormModel.destinationLocation!.id;
                                            });

                                        updatedSearchFormModel.withUpdatedSourceAndDestination(matchedSource, destination == undefined ? null : destination)
                                    } else {
                                        updatedSearchFormModel.withUpdatedSourceAndDestination(null, null)
                                    }
                                } else {
                                    const matchedSource = sourceLocations.find(source => {
                                        return source.id === stateContext.searchFormModel.sourceLocation!.id
                                    })
                                    if (matchedSource != undefined) {
                                        let destination = stateContext.searchFormModel.destinationLocation == null ? null :
                                            matchedSource.destinations.find(dest => {
                                                return dest.id === stateContext.searchFormModel.destinationLocation!.id;
                                            });

                                        updatedSearchFormModel.withUpdatedSourceAndDestination(matchedSource, destination == undefined ? null : destination)
                                    } else {
                                        updatedSearchFormModel.withUpdatedSourceAndDestination(null, null)
                                    }

                                }

                            }
                            subFsm.processEvent("booking_category_changed", { isSearchingSmallCar: isSearchingSmallCar, updatedSearchFormModel: updatedSearchFormModel });
                        }
                    },
                    onSearch: (searchCriteria: SearchCriteria) => {
                        subFsm.processEvent("search_requested", { searchCriteria: searchCriteria });
                    },
                    collapsedActionItems: buildMenuItems(appContext, subFsm, isAccountSupported),
                    onDevModeRequested: () => {
                        console.log("Triggering developer mode event");
                        window.dispatchEvent(new CustomEvent("dev-mode-on"))
                    },
                    onBackPressed: () => {
                        window.dispatchEvent(new CustomEvent("booking-flow-exit"))
                    }
                });
            }
            else if (substate == 'fetching_article' || substate == 'showing_article') {
                console.log("load", stateContext.articleToLoad);
                return m(ArticlePage, {
                    appContext: appContext,
                    title: stateContext.articleToLoad!.title,
                    article: stateContext.article,
                    onBackPressed: () => {
                        subFsm.processEvent('back', {});
                    }
                })
            }
            else if (substate == 'showing_contact') {
                return m(ContactPage, {
                    appContext: appContext,
                    onBackPressed: () => {
                        subFsm.processEvent('back', {});
                    },
                    onReload: () => {
                        subFsm.processEvent('contact_requested', {});
                    }
                })
            }
            else if (substate == 'selecting_location') {
                return m(LocationSelectionPage, {
                    appContext: appContext,
                    messageProvider: appContext.getMessageProvider(),
                    title: stateContext.selectingLocationType == 'source' ?
                        appContext.getMessageProvider().get("search.choose_source_location") :
                        appContext.getMessageProvider().get("search.choose_destination_location"),
                    locations: stateContext.selectingLocationType == 'source' ?
                        (stateContext.isSearchingSmallCar ? shuttleSources : sourceLocations)
                        : stateContext.searchFormModel.sourceLocation?.destinations || [],
                    onLocationSelected: (location: LocationDTO) => {
                        subFsm.processEvent("location_selected", {
                            locationType: stateContext.selectingLocationType!,
                            location: location
                        });
                    },
                    onClose: () => {
                        subFsm.processEvent('back', {});
                    }
                });
            }
            else if (substate == 'fetching_booking_details' || substate == 'showing_my_booking_details') {
                return m(BookingDetailsPage, {
                    appContext: appContext,
                    bookingDetails: currentMyBooking,
                    onBackPressed: () => {
                        subFsm.processEvent('back', {});
                    }
                });
            } else if (substate == 'fetching_my_booking_list' || 'showing_my_booking_list') {
                return m(BookingListPage, {
                    appContext: appContext,
                    status: fetchingMyBookings ? 'fetching' : 'showing',
                    bookings: myBookings,
                    onBackPressed: () => {
                        subFsm.processEvent('back', {});
                    },
                    onViewDetails: (booking: BookingSummaryDTO) => {
                        requestedBookingId = booking.id;
                        subFsm.processEvent('my_booking_details_requested', {})
                    }
                })
            }
            else {
                return m("div", `Not supported state ${substate}`)
            }
        }
    }
}

function buildMenuItems(appContext: AppContext, subFsm: SubFsm<HomeStateFsmScheme>, isAccountSupported: boolean = false): ActionItemDescriptor[] {
    const messageProvider = appContext.getMessageProvider();

    if (isAccountSupported) {
        return [
            {
                "label": messageProvider.get("nav_menu.my_bookings"),
                onclick: () => {
                    subFsm.processEvent('my_bookings_requested', {})
                }
            },
            {
                "label": messageProvider.get("nav_menu.faq"),
                onclick: () => {
                    subFsm.processEvent('article_requested', {
                        slug: 'frequently_asked_questions',
                        title: toIntlStringObject(messageProvider, "nav_menu.faq")
                    })
                }
            },
            {
                "label": messageProvider.get("nav_menu.terms_conditions"),
                onclick: () => {
                    subFsm.processEvent('article_requested', {
                        slug: 'terms_and_conditions',
                        title: toIntlStringObject(messageProvider, "nav_menu.terms_conditions")
                    })
                }
            },
            {
                "label": messageProvider.get("nav_menu.contact"),
                onclick: () => {
                    subFsm.processEvent('contact_requested', {});
                    subFsm.processEvent('article_requested', {
                        slug: 'contact',
                        title: toIntlStringObject(messageProvider, "nav_menu.contact")
                    })
                }
            }
        ];
    } else {

        let list = appContext.getExternalMenuItems().map(it => {
            return {
                "label": getIntlStringValue(it.label, messageProvider.getCurrentLang()),
                onclick: () => {
                    console.debug("link: ", it.href)
                    window.location.assign(it.href);
                }
            }
        })
        console.info("ext links", list);
        return list;
    }
}

function toIntlStringObject(messageProvider: MessageProvider, key: string): InternationalStringDTO {
    return {
        en: messageProvider.get(key, {}, "en"),
        my: messageProvider.get(key, {}, "my"),
    }
}